/**
 * @fileoverview FacilityLink — Vendor Network frontend service
 *
 * API client for trades, global vendor directory, and
 * per-organization vendor enrollment (My Vendor Network).
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

const BASE = `${VITE_CORE_API_URL}${CORE_URL}/organization/facility-link`;

// ─── Helpers ────────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
	const token = await auth?.currentUser?.getIdToken();
	if (!token) throw new Error("Authentication required");
	return token;
}

async function apiGet<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "POST",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiPut<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "PUT",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiDelete<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Trade {
	id: number;
	name: string;
	slug: string;
	icon: string | null;
	sortOrder: number;
}

export interface Vendor {
	id: number;
	companyName: string;
	contactName: string | null;
	contactEmail: string | null;
	contactPhone: string | null;
	website: string | null;
	description: string | null;
	logoUrl: string | null;
	isVerified: boolean;
	createdByOrgId: number | null;
	createdAt: string;
	updatedAt: string;
	trades: Trade[];
}

export interface OrgVendor {
	id: number;
	organizationId: number;
	vendorId: number;
	nickname: string | null;
	hourlyRate: number | null;
	priorityRank: number;
	isPreferred: boolean;
	isActive: boolean;
	notes: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface OrgVendorWithDetails extends OrgVendor {
	companyName: string;
	contactName: string | null;
	contactEmail: string | null;
	contactPhone: string | null;
	website: string | null;
	description: string | null;
	isVerified: boolean;
	trades: Trade[];
}

// ─── Trades ─────────────────────────────────────────────────────────────────

export async function listTrades(): Promise<Trade[]> {
	const data = await apiGet<{ trades: Trade[] }>("/trades");
	return data.trades;
}

// ─── Global Vendor Directory ────────────────────────────────────────────────

export async function searchGlobalVendors(
	search?: string,
	tradeId?: number,
): Promise<Vendor[]> {
	const params = new URLSearchParams();
	if (search) params.set("search", search);
	if (tradeId) params.set("tradeId", String(tradeId));
	const qs = params.toString();
	const data = await apiGet<{ vendors: Vendor[] }>(`/vendors${qs ? `?${qs}` : ""}`);
	return data.vendors;
}

export async function getVendorDetail(id: number): Promise<Vendor> {
	return apiGet<Vendor>(`/vendors/${id}`);
}

export async function createGlobalVendor(input: {
	companyName: string;
	contactName?: string;
	contactEmail?: string;
	contactPhone?: string;
	website?: string;
	description?: string;
	tradeIds?: number[];
}): Promise<Vendor> {
	return apiPost<Vendor>("/vendors", input);
}

// ─── My Vendor Network ──────────────────────────────────────────────────────

export async function listMyVendors(
	tradeId?: number,
	search?: string,
): Promise<OrgVendorWithDetails[]> {
	const params = new URLSearchParams();
	if (search) params.set("search", search);
	if (tradeId) params.set("tradeId", String(tradeId));
	const qs = params.toString();
	const data = await apiGet<{ vendors: OrgVendorWithDetails[] }>(`/my-vendors${qs ? `?${qs}` : ""}`);
	return data.vendors;
}

export async function enrollVendor(input: {
	vendorId: number;
	nickname?: string;
	hourlyRate?: number;
	isPreferred?: boolean;
	notes?: string;
}): Promise<OrgVendor> {
	return apiPost<OrgVendor>("/my-vendors/enroll", input);
}

export async function createAndEnrollVendor(input: {
	companyName: string;
	contactName?: string;
	contactEmail?: string;
	contactPhone?: string;
	website?: string;
	description?: string;
	tradeIds?: number[];
	nickname?: string;
	hourlyRate?: number;
	isPreferred?: boolean;
	notes?: string;
}): Promise<{ vendor: Vendor; orgVendor: OrgVendor }> {
	return apiPost<{ vendor: Vendor; orgVendor: OrgVendor }>("/my-vendors", input);
}

export async function updateMyVendor(
	orgVendorId: number,
	input: {
		nickname?: string;
		hourlyRate?: number;
		priorityRank?: number;
		isPreferred?: boolean;
		notes?: string;
	},
): Promise<OrgVendor> {
	return apiPut<OrgVendor>(`/my-vendors/${orgVendorId}`, input);
}

export async function removeMyVendor(orgVendorId: number): Promise<void> {
	await apiDelete(`/my-vendors/${orgVendorId}`);
}

// ─── Bulk Import ────────────────────────────────────────────────────────────

export interface ImportVendorRow {
	companyName: string;
	contactName?: string;
	contactEmail?: string;
	contactPhone?: string;
	website?: string;
	description?: string;
	trades?: string[];
}

export interface ImportResult {
	imported: number;
	enrolled: number;
	errors: { row: number; message: string }[];
}

export async function importVendors(
	vendors: ImportVendorRow[],
	autoEnroll: boolean,
): Promise<ImportResult> {
	return apiPost<ImportResult>("/import", { vendors, autoEnroll });
}
